import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ApiResponse } from '../../types/apiTypes';
import {
  RedFlag,
  RedFlagData,
  RedFlagLog,
  RedFlagCount,
  CreateRedFlagPayload,
  UpdateRedFlagPayload,
  CreateRedFlagDataPayload,
  UpdateFlagDataPayload,
  CreateRedFlagLogPayload,
} from '../../types/redFlagTypes';

export interface RedFlagState {
  redFlags: RedFlag[];
  matchingRedFlags: ApiResponse<RedFlag>;
  selectedRedFlag: RedFlag | null;
  selectedRedFlaggedVisitor: unknown | null;
  redFlagData: RedFlagData[];
  redFlagLogs: RedFlagLog[];
  redFlagCount: ApiResponse<RedFlagCount>;
  fetchRedFlagsLoading: boolean;
  describeRedFlagLoading: boolean;
  createRedFlagLoading: boolean;
  updateRedFlagLoading: boolean;
  fetchRedFlagDataLoading: boolean;
  createRedFlagDataLoading: boolean;
  updateRedFlagDataLoading: boolean;
  fetchRedFlagLogsLoading: boolean;
  createRedFlagLogLoading: boolean;
  fetchRedFlagCountLoading: boolean;
  error: string | null;
}

const initialState: RedFlagState = {
  redFlags: [],
  matchingRedFlags: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  selectedRedFlag: null,
  selectedRedFlaggedVisitor: null,
  redFlagData: [],
  redFlagLogs: [],
  redFlagCount: { data: [], meta: null, totalCount: 0, filteredCount: 0 },
  fetchRedFlagsLoading: false,
  describeRedFlagLoading: false,
  createRedFlagLoading: false,
  updateRedFlagLoading: false,
  fetchRedFlagDataLoading: false,
  createRedFlagDataLoading: false,
  updateRedFlagDataLoading: false,
  fetchRedFlagLogsLoading: false,
  createRedFlagLogLoading: false,
  fetchRedFlagCountLoading: false,
  error: null,
};

const redFlagSlice = createSlice({
  name: 'redFlags',
  initialState,
  reducers: {
    // --- Saga Trigger Actions ---
    fetchRedFlags: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        [key: string]: unknown;
        isMatchingRedFlags?: boolean;
      }>,
    ) => {},
    describeRedFlag: (
      _state,
      _action: PayloadAction<{ orgId: number; redFlagId: number }>,
    ) => {},
    createRedFlag: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        redFlag: Partial<CreateRedFlagPayload>;
      }>,
    ) => {},
    updateRedFlag: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        redFlagId: number;
        redFlag: Partial<UpdateRedFlagPayload>;
      }>,
    ) => {},
    fetchRedFlagData: (
      _state,
      _action: PayloadAction<{ orgId: number; redFlagId: number }>,
    ) => {},
    createRedFlagData: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        redFlagId: number;
        redFlagData: Partial<CreateRedFlagDataPayload>;
      }>,
    ) => {},
    updateRedFlagData: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        redFlagId: number;
        dataId: number;
        redFlagData: Partial<UpdateFlagDataPayload>;
      }>,
    ) => {},
    fetchRedFlagLogs: (
      _state,
      _action: PayloadAction<{ orgId: number; redFlagId: number }>,
    ) => {},
    createRedFlagLog: (
      _state,
      _action: PayloadAction<{
        orgId: number;
        redFlagId: number;
        redFlagLog: CreateRedFlagLogPayload;
      }>,
    ) => {},
    fetchRedFlagCount: (
      _state,
      _action: PayloadAction<{ orgId: number }>,
    ) => {},

    setRedFlags: (state, action: PayloadAction<ApiResponse<RedFlag>>) => ({
      ...state,
      redFlags: action.payload.data,
    }),
    setMatchingRedFlags: (
      state,
      action: PayloadAction<ApiResponse<RedFlag>>,
    ) => ({
      ...state,
      matchingRedFlags: action.payload,
    }),
    setSelectedRedFlag: (
      state,
      action: PayloadAction<ApiResponse<RedFlag>>,
    ) => ({
      ...state,
      selectedRedFlag: action.payload.data[0] || null,
    }),
    setRedFlagData: (
      state,
      action: PayloadAction<ApiResponse<RedFlagData>>,
    ) => ({
      ...state,
      redFlagData: action.payload.data,
    }),
    setRedFlagLogs: (
      state,
      action: PayloadAction<ApiResponse<RedFlagLog>>,
    ) => ({
      ...state,
      redFlagLogs: action.payload.data,
    }),
    setRedFlagCount: (
      state,
      action: PayloadAction<ApiResponse<RedFlagCount>>,
    ) => ({
      ...state,
      redFlagCount: action.payload,
    }),
    setLoading: (
      state,
      action: PayloadAction<{ key: keyof RedFlagState; value: boolean }>,
    ) => ({
      ...state,
      [action.payload.key]: action.payload.value,
    }),

    // --- UI Sync Reducers ---
    setSelectedRedFlaggedVisitor(state, action: PayloadAction<unknown | null>) {
      return {
        ...state,
        selectedRedFlaggedVisitor: action.payload,
      };
    },
    clearSelectedRedFlag(state) {
      return {
        ...state,
        selectedRedFlag: initialState.selectedRedFlag,
        redFlagData: initialState.redFlagData,
        redFlagLogs: initialState.redFlagLogs,
      };
    },
    clearError: (state) => ({ ...state, error: null }),
    clearRedFlagState: () => initialState,
  },
});

export const {
  fetchRedFlags,
  describeRedFlag,
  createRedFlag,
  updateRedFlag,
  fetchRedFlagData,
  createRedFlagData,
  updateRedFlagData,
  fetchRedFlagLogs,
  createRedFlagLog,
  fetchRedFlagCount,
  setRedFlags,
  setMatchingRedFlags,
  setSelectedRedFlag,
  setRedFlagData,
  setRedFlagLogs,
  setRedFlagCount,
  setLoading,
  setSelectedRedFlaggedVisitor,
  clearSelectedRedFlag,
  clearError,
  clearRedFlagState,
} = redFlagSlice.actions;

export const redFlagActions = redFlagSlice.actions;
export default redFlagSlice.reducer;
